import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_MEDIA_QUERY_KEY } from '@/state/queries/media/use-get-media.ts';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import type { CreateMediaDto } from '@/types/dtos/media/request/create-media.dto.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import {
  GET_USER_CONTRIBUTORS_QUERY_KEY,
  GET_CONTRIBUTOR_SUMMARY_QUERY_KEY,
} from '@/state/queries/contributors/user-contributors';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto.ts';
import { getObjectId } from '@/helpers/object.id.helper';
import { patchCreatedMediaInQueryCache } from '@/state/mutations/media/patch-created-media-cache';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';

export const useCreateMediaMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newMedia: CreateMediaDto) => {
      const token = await getToken();
      return createMedia(newMedia, token ?? '');
    },
    onSuccess: (data, variables) => {
      // Assets lists read Zustand/playlist refs; lean WS playlist updates omit embedded media,
      // so insert the created media immediately (refetch still reconciles usageIds).
      patchCreatedMediaInQueryCache(queryClient, {
        media: data,
        parentId: variables.parentId,
        parentType: variables.parentType,
        playlistScope: variables.playlistScope,
        placement: variables.placement,
      });

      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: [GET_MEDIA_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY] });
      void invalidateSimulationCueSheet(queryClient);

      const parentTypeLower = variables.parentType.toLowerCase();

      // Invalidate specific session query when parentType is Session (like contributor mutations do)
      if (parentTypeLower === 'session') {
        queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY, variables.parentId] });
        queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY, variables.parentId], type: 'active' });
        // Also invalidate and refetch event sessions query to update the store
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
        queryClient.refetchQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY], type: 'active' });
      } else {
        // Fallback: invalidate all session queries if parentType is not Session
        queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
      }

      // Invalidate flow query with specific flowId when parentType is Flow (like contributor mutations do)
      if (parentTypeLower === 'flow') {
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, variables.parentId] });
        queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, variables.parentId], type: 'active' });
      } else if (parentTypeLower === 'session') {
        // When parentType is Session, find all flows that contain this session and invalidate them
        const sessionId = variables.parentId;
        const flowQueries = queryClient.getQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] });
        const flowsToInvalidate = new Set<string>();

        // Check all flow queries in cache to find flows containing this session
        for (const [queryKey, flowData] of flowQueries) {
          const flow = Array.isArray(flowData) ? flowData[0] : flowData;
          if (flow && typeof flow === 'object' && 'slots' in flow) {
            const flowDto = flow as FlowDto;
            const hasSession = flowDto.slots?.some((slot) => {
              if (slot.contentType !== 'Session') return false;
              const slotSessionId =
                typeof slot.content === 'string'
                  ? slot.content
                  : slot.content?.origin
                    ? getObjectId(slot.content.origin)
                    : slot.content?.id;
              const sessionOriginId = sessionId;
              return slotSessionId === sessionOriginId || slotSessionId === sessionId;
            });

            if (hasSession) {
              // Extract flowId from queryKey: [GET_FLOW_QUERY_KEY, flowId, eventSlotId?]
              const flowId =
                Array.isArray(queryKey) && queryKey.length > 1
                  ? queryKey[1]
                  : flowDto.id || flowDto.origin
                    ? getObjectId(flowDto.origin)
                    : undefined;
              if (flowId && typeof flowId === 'string') {
                flowsToInvalidate.add(flowId);
              }
            }
          }
        }

        // Also check event flows query cache
        const eventFlowsQueries = queryClient.getQueriesData({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
        for (const [, flowsData] of eventFlowsQueries) {
          const flows = Array.isArray(flowsData) ? flowsData : [];
          for (const flow of flows) {
            if (flow && typeof flow === 'object' && 'slots' in flow) {
              const flowDto = flow as FlowDto;
              const hasSession = flowDto.slots?.some((slot) => {
                if (slot.contentType !== 'Session') return false;
                const slotSessionId =
                  typeof slot.content === 'string'
                    ? slot.content
                    : slot.content?.origin
                      ? getObjectId(slot.content.origin)
                      : slot.content?.id;
                const sessionOriginId = sessionId;
                return slotSessionId === sessionOriginId || slotSessionId === sessionId;
              });

              if (hasSession) {
                const flowId = flowDto.id || (flowDto.origin ? getObjectId(flowDto.origin) : undefined);
                if (flowId) {
                  flowsToInvalidate.add(flowId);
                }
              }
            }
          }
        }

        // Invalidate all flows that contain this session
        flowsToInvalidate.forEach((flowId) => {
          queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId] });
          queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId], type: 'active' });
        });

        // Fallback: if no flows found in cache, invalidate all flow queries
        if (flowsToInvalidate.size === 0) {
          queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
          queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY], type: 'active' });
        }
      } else {
        // Fallback: invalidate all flow queries if parentType is not Flow or Session
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
        queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY], type: 'active' });
      }
    },
  });
};

const createMedia = async (newMedia: CreateMediaDto, token: string): Promise<MediaDto> => {
  const { parentType, playlistScope, ...rest } = newMedia;
  const body = parentType.toLowerCase() === 'session' ? rest : { ...rest, ...(playlistScope ? { playlistScope } : {}) };
  const url = `${import.meta.env.VITE_API_URL}/media/${parentType}`;
  return sendJSONRequest({ url, method: METHOD.POST, body, token });
};
